import { dimensionIndex, labelOf } from './dimensions';
import type { MentionRow } from './generator';

/**
 * Competitive page performance — one row per brand **page**, which is one brand
 * on one channel.
 *
 * The corpus has no notion of a page. It has mentions, each carrying a brand
 * and a channel, so "Robi's Facebook page" is every mention of Robi on
 * Facebook, and the page's posts are those mentions. That is the only reading
 * the data supports, and it is honest about what it counts: activity *around*
 * a brand on a channel, which is what a competitive comparison needs anyway —
 * a brand's own posting schedule says less about who is winning than the
 * conversation it draws.
 *
 * Five brands by seven channels caps this at 35 pages. The frame's badge reads
 * "50 total pages", which is example content; the badge here counts what the
 * table holds.
 */

export type PageRow = {
  /** `brand:channel`, stable across renders and filters. */
  id: string;
  brand: string;
  brandLabel: string;
  channel: string;
  channelLabel: string;
  /**
   * The page's **dominant** sentiment, intent and topic — the most common value
   * across its mentions, not a value the page itself carries.
   *
   * A page is a brand on a channel and has no tone of its own; its mentions do.
   * The frame added these three columns on 2026-09-23 and the reading settled
   * with the user is the modal value: "what is most of this conversation".
   * A page whose mentions are 40% negative, 35% neutral and 25% positive reads
   * `Negative`, which is true of the plurality and silent about the rest — the
   * sentiment comparison card above is where the mix is read.
   *
   * Ties break on **fixture order**, the same rule `pageList`'s own sort uses,
   * so a page split evenly between two topics names the same one on every
   * render instead of trading between them.
   */
  sentiment: string;
  sentimentLabel: string;
  intent: string;
  intentLabel: string;
  topic: string;
  topicLabel: string;
  posts: number;
  engagement: number;
  /**
   * Reactions **including likes**. The corpus splits engagement four ways —
   * shares, comments, reactions, likes — and the frame draws columns for only
   * three. Folding likes into reactions is how the platforms count them (a like
   * *is* a reaction on Facebook), and it is what makes Reactions + Comments +
   * Shares add up to Total engagement on every row, rather than falling short by
   * a column nobody can see. Settled with the user 2026-09-18.
   */
  reactions: number;
  comments: number;
  shares: number;
  /**
   * This page's engagement as a percentage of **every** page in the table — so
   * it is a share of all 35 pages, not just the ten on screen.
   *
   * No column renders it since 2026-09-23: the frame's `Engagement share`
   * column turned out to hold counts, and the user confirmed it is
   * `Total engagement`. Kept because it costs one division and the card above
   * reads the same quantity — dropping it would only move the arithmetic.
   */
  share: number;
};

/**
 * The most common value of one dimension across a page's mentions.
 *
 * Ties break on fixture order — see `PageRow.sentiment`. `dimensionIndex` is
 * the same table the chip order and the series colours come from, so "first"
 * means the same thing here as everywhere else.
 */
function dominant(counts: Map<string, number>, key: 'sentiment' | 'intent' | 'topic'): string {
  let best = '';
  let bestCount = -1;

  for (const [value, count] of counts) {
    if (count > bestCount) {
      best = value;
      bestCount = count;
      continue;
    }
    if (count !== bestCount) continue;

    const order = dimensionIndex[key];
    if ((order[value] ?? 0) < (order[best] ?? 0)) best = value;
  }

  return best;
}

/** One tally per dimension, built as the rows are walked. */
type Tallies = { sentiment: Map<string, number>; intent: Map<string, number>; topic: Map<string, number> };

const bump = (counts: Map<string, number>, value: string) =>
  counts.set(value, (counts.get(value) ?? 0) + 1);

export function pageList(rows: readonly MentionRow[]): PageRow[] {
  type Acc = Omit<
    PageRow,
    | 'share'
    | 'brandLabel'
    | 'channelLabel'
    | 'sentiment'
    | 'sentimentLabel'
    | 'intent'
    | 'intentLabel'
    | 'topic'
    | 'topicLabel'
  >;
  const byPage = new Map<string, Acc>();
  /* Kept beside the accumulator rather than on it: three maps are not a row. */
  const tallies = new Map<string, Tallies>();

  for (const row of rows) {
    const id = `${row.brand}:${row.channel}`;
    let page = byPage.get(id);
    if (!page) {
      page = {
        id,
        brand: row.brand,
        channel: row.channel,
        posts: 0,
        engagement: 0,
        reactions: 0,
        comments: 0,
        shares: 0,
      };
      byPage.set(id, page);
      tallies.set(id, { sentiment: new Map(), intent: new Map(), topic: new Map() });
    }

    const tally = tallies.get(id)!;
    bump(tally.sentiment, row.sentiment);
    bump(tally.intent, row.intent);
    bump(tally.topic, row.topic);

    page.posts += 1;
    page.engagement += row.engagement;
    page.reactions += row.reactions + row.likes;
    page.comments += row.comments;
    page.shares += row.shares;
  }

  /* The denominator is the whole table, reduced from the same pages it divides. */
  let total = 0;
  for (const page of byPage.values()) total += page.engagement;

  return (
    [...byPage.values()]
      /*
       * Fixture order — brand, then channel — as the *unsorted* order. The view
       * sorts on top of it; this only decides how ties fall, so two pages with
       * equal engagement never swap places between renders.
       */
      .sort(
        (a, b) =>
          (dimensionIndex.brand[a.brand] ?? 0) - (dimensionIndex.brand[b.brand] ?? 0) ||
          (dimensionIndex.channel[a.channel] ?? 0) - (dimensionIndex.channel[b.channel] ?? 0),
      )
      .map((page) => {
        const tally = tallies.get(page.id)!;
        const sentiment = dominant(tally.sentiment, 'sentiment');
        const intent = dominant(tally.intent, 'intent');
        const topic = dominant(tally.topic, 'topic');

        return {
          ...page,
          brandLabel: labelOf('brand', page.brand),
          channelLabel: labelOf('channel', page.channel),
          sentiment,
          sentimentLabel: labelOf('sentiment', sentiment),
          intent,
          intentLabel: labelOf('intent', intent),
          topic,
          topicLabel: labelOf('topic', topic),
          share: total === 0 ? 0 : (page.engagement / total) * 100,
        };
      })
  );
}
